import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  UpdateAssessmentInput,
  UpdateOversightAssessmentMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  Assessment_Status_Enum,
  UpdateOversightAssessmentDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

/**
 * The hook's public input: the legacy GraphQL input plus the
 * optimistic-concurrency token (COI-622), which rides only on the tRPC leg —
 * the legacy mutation input has no such field.
 */
type WrapperInput = UpdateAssessmentInput & {
  OriginalTimestamp?: string | null;
};

/**
 * tRPC input type for updateOversightAssessment. Assessment has no schedule,
 * so no schedule transformation is needed. The legacy action returns
 * affected_rows (NOT an IdOutput).
 */
type UpdateOversightAssessmentTRPCInput = {
  Id: string;
  Title: string;
  Status: Assessment_Status_Enum;
  /** Optimistic-concurrency token (COI-622) — only this path locks on it. */
  OriginalTimestamp?: string | null;
  OriginatingItemId?: string | null;
  Summary?: string | null;
  ActualCompletionDate?: string | null;
  NextTestDate?: string | null;
  StartDate?: string | null;
  TargetCompletionDate?: string | null;
  CompletedByUser?: string | null;
  Outcome?: number | null;
  CustomAttributeData?: CustomAttributeData | null;
  OwnerUserIds?: string[];
  OwnerGroupIds?: string[];
  ContributorUserIds?: string[];
  ContributorGroupIds?: string[];
  ViewerUserIds?: string[] | null;
  ViewerGroupIds?: string[] | null;
  TagTypeIds?: string[];
  DepartmentTypeIds?: string[];
};

export const useUpdateOversightAssessment = createMutationHook({
  name: 'updateOversightAssessment',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.oversight.update.mutationOptions({ onSuccess }),
  mapInput: (
    variables: WrapperInput
  ): UpdateOversightAssessmentTRPCInput => ({
    ...variables,
    Title: variables.Title ?? '',
    Status: variables.Status ?? Assessment_Status_Enum.Notstarted,
  }),
  invalidate: (trpc) => [
    trpc.frontend.oversight.register.queryKey(),
    trpc.frontend.oversight.getById.queryKey(),
    // COI-471-style parity: the Apollo twin also evicts the base
    // assessment-result families (their ratings roll up into the
    // assessment) and re-fetches oversight-specific result reads that ARE
    // migrated. `GetOversightResultsByParentIdDocument` and
    // `GetAllOversightAssessmentResultsDocument` have NO tRPC twin yet
    // (RSP-6476 Cluster 2 thinking-point #1) — those two result-list reads
    // cannot be invalidated from this path until they migrate.
    trpc.frontend.oversight.oversightAssessmentCount.queryKey(),
    trpc.frontend.assessment.oversightAssessmentResultCount.queryKey(),
    trpc.frontend.assessment.oversightAssessmentRiskAssessmentResultsByRiskId.queryKey(),
    trpc.frontend.assessment.oversightAssessmentDocumentAssessmentResultsByDocumentId.queryKey(),
    trpc.frontend.assessment.oversightAssessmentObligationAssessmentResultsByObligationId.queryKey(),
  ],
  mapResponse: (result: {
    affected_rows: number;
  }): UpdateOversightAssessmentMutation => ({
    updateOversightAssessmentApi: {
      __typename: 'GenericMutationOutput',
      affected_rows: result.affected_rows,
    },
  }),
  graphqlDocument: UpdateOversightAssessmentDocument,
  // COI-622: strip the concurrency token — the legacy mutation input has no
  // OriginalTimestamp field, so only the tRPC leg locks on it.
  graphqlVariables: ({
    OriginalTimestamp: _token,
    ...variables
  }: WrapperInput) => ({
    object: variables,
  }),
  // The corresponding READS are NOT migrated and are cache-first, so a write
  // via tRPC still has to evict these or the Apollo-backed list/counter shows
  // pre-write data. Kept identical on both branches so they cannot drift.
  evictFields: [
    'risk_assessment_result',
    'document_assessment_result',
    'obligation_assessment_result',
    'oversight_assessment',
  ],
});
